import {
  useEffect, useRef, useState, 
} from 'react';
import { useTranslation } from 'react-i18next';

import MaskInput from 'components/MaskedInput';
import { formatSwissNumber } from 'utils/formatNumber';
import Input from 'components/Input';
  
interface StatusBoardSoilPhSectionProps {
  isGreaterTarget?: boolean;
  getMthaText?: string;
  getMtha: string | '';
  getOptimumValue: string;
  isExpertMode?: boolean;
  isMaintenanceMode?: boolean;
}

export const StatusBoardSoilPhSection = ({
  getMtha,
  getOptimumValue,
  isMaintenanceMode,
}: StatusBoardSoilPhSectionProps) => {
  const { t } = useTranslation();
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);

  // Detect screen size for responsive tooltip width
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node)) {
        setShowTooltip(false);
      }
    };

    if (showTooltip) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTooltip]);

  return (
    <div className="flex justify-between md:flex-col py-8"> 
      <div className="w-1/2 border-r border-primitiveGrey md:border-b md:border-r-0 pr-8 md:w-full md:pr-0 pb-0">
        <div className="text-black font-bold text-xl md:text-lg md-2">
          {t('carbonateAgronomic')}{' '}
          <span 
            className="cursor-pointer font-black relative inline-block"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            ref={tooltipRef}
          >
            ⓘ
            {showTooltip && (
              <div
                className="absolute left-1/2 transform -translate-x-1/2 bg-white shadow-lg"
                style={{
                  bottom: '100%',
                  width: isMobile ? '65vw' : '650px',
                  maxWidth: '80vw',
                  maxHeight: '600px',
                  padding: '16px',
                  border: '1px solid #cbcbcb',
                  borderRadius: '12px',
                  textAlign: 'center',
                  marginBottom: '15px',
                  zIndex: 1000,
                  fontWeight: 400,
                }}
              >
                <div style={{ maxWidth: '100%',
                  maxHeight: '600px' }}>
                  <div style={{
                    marginBottom: '12px',
                    textAlign: 'left',
                    color: '#000',
                    fontSize: '14px',
                  }}>
                    Pour obtenir les gains potentiels, apporter {formatSwissNumber(Number(getMtha))} t de VN efficace / ha en choisissant le bon produit grâce à l&apos;index CAP :
                  </div>
                  <img
                    src="/5YearsProjGraphFrench.png"
                    style={{
                      height: 'auto',
                      maxHeight: '600px',
                      minHeight: '400px',
                      display: 'block',
                      margin: '0 auto',
                    }}
                    alt="5 Years Projection Graph"
                  />
                </div>
              </div>
            )}
          </span>
        </div>
        <div className="my-6">
          <Input
            titleClassName="top-[-9px] left-[8px] bg-disable-gradient"
            title={t('optimum')}
            className="w-full"
            disabled
            value={getOptimumValue}
            isBlueBg
          />
        </div>
      </div>
      
      <div className="w-1/2 pl-8 md:w-full md:pl-0">
        <div className="text-black font-bold text-xl md:text-lg mb-6 md:mt-6">
          {isMaintenanceMode ? t('maintainingSoilPh') : t('improvementSoilPh')}
        </div>
        <MaskInput
          titleClassName="top-[-9px] left-[8px] bg-disable-gradient truncate max-w-[85%]"
          title={`Total U VN en ${isMaintenanceMode ? 'kg' : 't'}/ha`}
          className="w-full"
          disabled
          isBlueBg
          value={formatSwissNumber(Number(getMtha))}
        />
      </div>
    </div>
  );
};
